import { useMemo } from "react";
import type { Control, UseFormSetValue } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { ReactSortable } from "react-sortablejs";
import { PlusIcon } from "@heroicons/react/outline";
import { MdDeleteOutline, MdDragIndicator } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Input, Checkbox } from "../../../../common/components/Input";
import { Tooltip } from "../../../../common/components/Tooltip/Tooltip";
import InfoIconWithTooltip from "../../../../common/components/Icon/InfoIconWithTooltip";

// content-zone-sort and content-zone-detect both store a spatial `hotspot_info`
// array. Hotspot PLACEMENT (rotation_*/position_*) is authored in the Spatial
// Studio and never edited here; every placement key (and any key not listed
// below) is passed through UNTOUCHED on write.
//
// variant "zone-sort" items:
//   { index, id_internal, explanation, font_size, scale,
//     rotation_x, rotation_y, rotation_z, position_x?, position_y?, position_z? }
//   • explanation — translatable text (editable in every language)
//   • index       — the CORRECT sort position (1-based). Reordering (source
//                   language only, drag-and-drop like the quizz-sort editor)
//                   recomputes it so it stays unique + contiguous 1..N.
//   • id_internal — stable identity, preserved untouched.
//   Rows are never added/removed here — the set of zones comes from VR.
//
// variant "zone-detect" items:
//   { idInternal?, explanation, is_true_response, font_size, scale,
//     rotation_x, rotation_y, rotation_z, position_x?, position_y?, position_z? }
//   • explanation      — translatable text (editable in every language)
//   • is_true_response — marks which hotspots are real errors (structural flag,
//                        source language only). ~4% of live rows LACK idInternal,
//                        so rows are keyed by ARRAY POSITION, never by idInternal.
//   • zones CAN be created/deleted here (2026-07-16 ruling) — new zones spawn
//     with the content-type default values (scale 0.05, font_size 23, zeroed
//     placement) and are positioned afterwards in the Spatial Studio; the (?)
//     tooltip next to the Add button says exactly that. Min 1 zone.
//
// The array is written straight back to react-hook-form so the existing save
// path round-trips it (content PUT + PLD sync).

export interface HotspotInfoFieldProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  label: string;
  /** View mode / mid-generation — fully read-only. */
  disabled?: boolean;
  /** Structural edits (reorder, add/delete, is_true_response) are source-language only. */
  isSourceLanguage?: boolean;
  /** Optional per-explanation character cap. */
  maxLength?: number;
  /** Drives which item shape + rules apply. */
  variant: "zone-sort" | "zone-detect";
}

// A hotspot item. All keys are optional/passthrough except the ones this editor
// mutates; unknown keys are preserved via spread on write.
interface HotspotItem {
  explanation?: string;
  index?: number;
  id_internal?: number;
  idInternal?: number;
  is_true_response?: boolean;
  [key: string]: unknown;
}

// Transient row shape for ReactSortable (zone-sort): a stable `id` glued onto
// the item, stripped again before writing back to RHF.
type SortableRow = HotspotItem & { id: number | string };

// Default shape of a freshly-created zone-detect zone — verbatim the
// content-type's `content_type_default_values.hotspot_info[0]` (what VR seeds
// on creation), minus idInternal which is assigned max+1 per content.
const newZoneDefaults = {
  explanation: "",
  is_true_response: true,
  font_size: 23,
  scale: 0.05,
  rotation_x: 0,
  rotation_y: 0,
  rotation_z: 0,
  position_x: 0,
  position_y: 0,
  position_z: 0,
};

export default function HotspotInfoField({
  control,
  setValue,
  label,
  disabled = false,
  isSourceLanguage = true,
  maxLength,
  variant,
}: HotspotInfoFieldProps) {
  const { t } = useTranslation();

  const watched = useWatch({ control, name: "hotspot_info" });

  // RHF is the single source of truth. Derive display rows from it. For
  // zone-sort, present in ascending `index` order (top = position 1); zone-detect
  // keeps natural array order. Either way, the array position after this sort is
  // the canonical display position used by all handlers.
  const rows: HotspotItem[] = useMemo(() => {
    const arr: HotspotItem[] = Array.isArray(watched) ? watched : [];
    if (variant !== "zone-sort") return arr;
    return [...arr].sort((a, b) => {
      const ai = typeof a?.index === "number" ? a.index : 0;
      const bi = typeof b?.index === "number" ? b.index : 0;
      return ai - bi;
    });
  }, [watched, variant]);

  // Text (explanation) is editable in any language; structural controls
  // (reorder, add/delete, is_true_response) are locked outside the source language.
  const structureLocked = disabled || !isSourceLanguage;

  const write = (next: HotspotItem[]) => setValue("hotspot_info", next, { shouldDirty: true });

  // Reorder commit (zone-sort only): after a drag, recompute every row's
  // `index` from its visual position so values stay UNIQUE + CONTIGUOUS 1..N.
  // Every other key on each item (id_internal, explanation, placement, font/scale)
  // is preserved untouched.
  const commitOrder = (nextRows: HotspotItem[]) => {
    write(nextRows.map((r, i) => ({ ...r, index: i + 1 })));
  };

  const handleExplanationChange = (position: number, value: string) => {
    write(rows.map((r, i) => (i === position ? { ...r, explanation: value } : r)));
  };

  const handleToggleTrueResponse = (position: number, checked: boolean) => {
    write(rows.map((r, i) => (i === position ? { ...r, is_true_response: checked } : r)));
  };

  const isSort = variant === "zone-sort";
  const isDetect = variant === "zone-detect";

  // ── zone-sort drag-and-drop (same UX as the quizz-sort editor) ──────────
  // Rows carry a stable transient `id` for ReactSortable / React keys —
  // id_internal is the natural unique-per-zone choice; stripped on write.
  const sortableRows: SortableRow[] = useMemo(
    () =>
      rows.map((r, i) => ({
        ...r,
        id: typeof r.id_internal === "number" ? r.id_internal : `row-${i}`,
      })),
    [rows],
  );

  const handleSetList = (nextRows: SortableRow[]) => {
    // ReactSortable also calls setList on internal normalization (same order) —
    // ignore those to avoid a redundant dirtying write; only commit real drags.
    const sameOrder =
      nextRows.length === sortableRows.length &&
      nextRows.every((r, i) => String(r.id) === String(sortableRows[i].id));
    if (sameOrder) return;
    commitOrder(nextRows.map(({ id: _id, ...rest }) => rest));
  };

  // ── zone-detect create/delete ────────────────────────────────────────────
  const handleAddZone = () => {
    // New zones get idInternal = max + 1 (stable, never reused; some legacy
    // rows lack the key — they simply don't participate in the max).
    const maxIdInternal = rows.reduce(
      (m, r) => (typeof r.idInternal === "number" ? Math.max(m, r.idInternal) : m),
      -1,
    );
    write([...rows, { ...newZoneDefaults, idInternal: maxIdInternal + 1 }]);
  };

  const handleRemoveZone = (position: number) => {
    if (rows.length <= 1) return;
    write(rows.filter((_, i) => i !== position));
  };

  const canAddZone = isDetect && !structureLocked;
  const canRemoveZone = isDetect && !structureLocked && rows.length > 1;
  const minZonesHint = t("pages.project.editNode.hotspotMinZonesHint") as string;

  const renderRow = (row: HotspotItem, position: number) => {
    const rowLabel = t("pages.project.editNode.hotspotItemLabel", {
      n: position + 1,
      defaultValue: `Hotspot ${position + 1}`,
    });

    return (
      <div
        key={isSort ? (row as SortableRow).id : position}
        className="border border-gray-200 rounded-lg p-3 bg-white"
      >
        <div className="flex items-center gap-3">
          {isSort && (
            <>
              <Tooltip
                message={
                  structureLocked ? "" : (t("pages.project.editNode.quizzSortDragHandle") as string)
                }
                className="shrink-0"
              >
                <span
                  className={`hotspot-sort-drag-handle flex items-center text-gray-400 ${
                    structureLocked ? "cursor-not-allowed opacity-40" : "cursor-grab"
                  }`}
                  aria-hidden="true"
                >
                  <MdDragIndicator size={20} />
                </span>
              </Tooltip>

              {/* Correct-position badge — reflects the row's drag order. */}
              <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 text-sm font-semibold">
                {position + 1}
              </span>
            </>
          )}

          <div className="flex-1 min-w-0">
            <Input
              type="text"
              label={rowLabel}
              value={typeof row.explanation === "string" ? row.explanation : ""}
              onChange={(e) => handleExplanationChange(position, e.target.value)}
              disabled={disabled}
              maxLength={maxLength}
              className="dynamic-field"
            />
          </div>

          {isDetect && (
            <Tooltip
              message={!canRemoveZone && !structureLocked ? minZonesHint : ""}
              className="shrink-0"
              classNameChildren="right-0"
            >
              <button
                type="button"
                onClick={() => handleRemoveZone(position)}
                disabled={!canRemoveZone}
                className="text-red-500 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label={t("pages.project.editNode.hotspotDeleteZone") as string}
              >
                <MdDeleteOutline size={20} />
              </button>
            </Tooltip>
          )}
        </div>

        {isDetect && (
          <div className="mt-1">
            <Tooltip
              message={
                structureLocked
                  ? (t("pages.project.editNode.quizzStructureLockedHint") as string)
                  : ""
              }
            >
              <Checkbox
                label={
                  t("pages.project.editNode.hotspotIsTrueResponse", {
                    defaultValue: "This hotspot is a real error",
                  }) as string
                }
                checked={row.is_true_response === true}
                disabled={structureLocked}
                onChange={(e) => handleToggleTrueResponse(position, e.target.checked)}
              />
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full mt-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
          {label}
          {isDetect ? (
            // Zone placement is a Spatial Studio concern — say so where the
            // zones are managed (2026-07-16 ruling).
            <InfoIconWithTooltip
              message={t("pages.project.editNode.hotspotPlacementSpatialStudioHint")}
            />
          ) : null}
        </label>
        {isDetect && (
          <button
            type="button"
            onClick={handleAddZone}
            disabled={!canAddZone}
            className="btn-alternative-outline flex items-center gap-1.5 text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            <span>{t("pages.project.editNode.hotspotAddZone")}</span>
          </button>
        )}
      </div>

      {structureLocked && !disabled && (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.quizzStructureLockedHint")}
        </p>
      )}

      {isSort && !structureLocked && (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.hotspotReorderHint")}
        </p>
      )}

      {isSort ? (
        <ReactSortable
          list={sortableRows}
          setList={handleSetList}
          handle=".hotspot-sort-drag-handle"
          disabled={structureLocked}
          animation={150}
          className="space-y-3"
        >
          {sortableRows.map((row, position) => renderRow(row, position))}
        </ReactSortable>
      ) : (
        <div className="space-y-3">{rows.map((row, position) => renderRow(row, position))}</div>
      )}
    </div>
  );
}
